/* Texte der Kacheln auf Meine Woche und in der Übersicht einer Kapsel (BAUPLAN-FEINSCHLIFF.md, Abschnitte 4.1 und 4.2). Ohne React,
   mit Vitest getestet. Aufbau je Kachel: Oberzeile, Hauptzeile, Stand. */
import { tagMitWochentag } from "../../kern/kalender";
import { heute, tageBis } from "../../kern/zeit";
import type { Aufgabe } from "./logik";
import type { SprintFortschritt } from "./sprintlogik";
import { alsNaechstes, countdown, fristAbstand, type VorhabenKarte, type VorhabenRolle } from "./vorhabenlogik";

/** „Sa. 05.12.“, in einem anderen Jahr „Sa. 19.06.2027“ */
export function datumKurz(tag: string, heuteTag: string = heute()): string {
  return tag.slice(0, 4) === heuteTag.slice(0, 4) ? tagMitWochentag(tag) : `${tagMitWochentag(tag)}${tag.slice(0, 4)}`;
}

/** Fortschritt als Zahl und Text: „2 von 20“ erledigt, 10 %. Ohne Aufgaben kein Balken. */
export interface Stand {
  /** „2 von 20“, fett in der Kachel. Leer: ohne Aufgaben */
  zahl: string;
  /** „erledigt, 10 %“ oder „Noch keine Aufgaben“ */
  text: string;
  /** 0 bis 100, ohne Aufgaben null (freier Platz statt Balken) */
  prozent: number | null;
}

/** Sprint: „2 von 20“ erledigt, 10 % */
export function sprintStand(f: Pick<SprintFortschritt, "gesamt" | "erledigt" | "prozent">): Stand {
  if (!f.gesamt) return { zahl: "", text: "Noch keine Aufgaben", prozent: null };
  return { zahl: `${f.erledigt} von ${f.gesamt}`, text: `erledigt, ${f.prozent} %`, prozent: f.prozent };
}

/** Oberzeile der Sitzung: „Sitzung · in 10 Tagen“, „Sitzung · morgen“, „Sitzung · heute“ */
export function sitzungOber(datum: string, heuteTag: string = heute()): string {
  return `Sitzung · ${fristAbstand(datum, heuteTag)}`;
}

/** Stand der Sitzung in einer Zeile wie bisher unter dem Datum: „Vorstandssitzung, 19:00 Uhr, Hofraum“ */
export function sitzungStand(t: { uhrzeit: string | null; ort: string | null }): string {
  return ["Vorstandssitzung", t.uhrzeit ? `${t.uhrzeit.slice(0, 5)} Uhr` : null, t.ort || null].filter(Boolean).join(", ");
}

/** Oberzeile einer eigenen Kapsel: „Vorhaben · Du leitest“ oder „Vorhaben · Du machst mit“ */
export function vorhabenOber(rolle: VorhabenRolle | null): string {
  return `Vorhaben · ${rolle === "leitend" ? "Du leitest" : "Du machst mit"}`;
}

/** Termin kurz für den Stand: „bis Do. 31.12.“, „bis heute“, „vorbei“, „ab Mo. 02.11.“, „laufend“ oder „Datum offen“ */
export function bisText(v: Pick<VorhabenKarte, "beginn" | "ende">, heuteTag: string = heute()): string {
  if (v.ende) {
    const n = tageBis(v.ende, heuteTag);
    if (n < 0) return "vorbei";
    if (n === 0) return "bis heute";
    return `bis ${datumKurz(v.ende, heuteTag)}`;
  }
  if (v.beginn) return v.beginn > heuteTag ? `ab ${datumKurz(v.beginn, heuteTag)}` : "laufend";
  return "Datum offen";
}

/** Stand einer eigenen Kapsel: „bis Do. 31.12., “ + „1 von 4“ + „erledigt“, ohne Aufgaben „…, noch keine Aufgaben“ */
export function vorhabenStand(v: Pick<VorhabenKarte, "beginn" | "ende" | "aufgaben_gesamt" | "aufgaben_erledigt">, heuteTag: string = heute()) {
  const bis = bisText(v, heuteTag);
  if (!v.aufgaben_gesamt) return { vor: `${bis}, noch keine Aufgaben`, zahl: "", nach: "", prozent: null };
  return {
    vor: `${bis}, `,
    zahl: `${v.aufgaben_erledigt} von ${v.aufgaben_gesamt}`,
    nach: " erledigt",
    prozent: Math.round((v.aufgaben_erledigt / v.aufgaben_gesamt) * 100),
  };
}

/** Kachel „Termin“ in der Übersicht: Datum als Hauptzeile, darunter der Abstand („noch 50 Tage“, „endet heute“, „vorbei“) */
export function terminKachel(v: Pick<VorhabenKarte, "beginn" | "ende">, heuteTag: string = heute()): { haupt: string; stand: string } {
  const c = countdown(v, heuteTag);
  if (v.ende) return { haupt: datumKurz(v.ende, heuteTag), stand: c.kurz };
  if (v.beginn && v.beginn > heuteTag) return { haupt: datumKurz(v.beginn, heuteTag), stand: "geplant, ohne Enddatum" };
  if (v.beginn) return { haupt: "laufend", stand: `seit ${datumKurz(v.beginn, heuteTag)}, ohne Enddatum` };
  return { haupt: "Datum offen", stand: "noch kein Termin eingetragen" };
}

/** Kachel „Aufgaben“ in der Übersicht: „1 von 4 erledigt“, darunter die nächste Frist einer offenen Aufgabe */
export function aufgabenKachel(
  v: Pick<VorhabenKarte, "id" | "aufgaben_gesamt" | "aufgaben_erledigt">,
  aufgaben: readonly Aufgabe[],
): { haupt: string; stand: string; prozent: number | null } {
  if (!v.aufgaben_gesamt) return { haupt: "Noch keine Aufgaben", stand: "Leg im Reiter Aufgaben die erste an.", prozent: null };
  const naechste = alsNaechstes(aufgaben, v.id);
  return {
    haupt: `${v.aufgaben_erledigt} von ${v.aufgaben_gesamt} erledigt`,
    stand: naechste?.frist_datum ? `nächste Frist ${tagMitWochentag(naechste.frist_datum)}` : "keine offene Frist",
    prozent: Math.round((v.aufgaben_erledigt / v.aufgaben_gesamt) * 100),
  };
}
